import { PaymentStatus } from '../enumerations';
import {
  handleUrl,
  myHeadersWithToken,
  myHeadersWithTokenContentType,
} from '../global';
import {
  AddAttachmentProps,
  DetailProps,
  ExportCSVParams,
  GetAllHivePaymentsParams,
  GetAllHivePaymentsProps,
  GetFinancialProps,
  GetHivePaymentProps,
  PaymentSummaryParams,
} from './types';

export const getFinancialSummary = ({
  search,
  period,
  pendingOnly,
}: PaymentSummaryParams): Promise<GetFinancialProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  const params: Record<string, string> = {};
  if (search) params.search_string = search;
  if (period) params.reference_date = period;
  if (pendingOnly) params.pending_only = String(pendingOnly);

  const queryString = new URLSearchParams(params).toString();
  return fetch(
    `${baseUrl}/api/V1/hive-payments/summary?${queryString}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getHivePayment = (id: number): Promise<GetHivePaymentProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/hive-payments/${id}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getAllHivePayments = ({
  page,
  size,
  search,
  period,
  status,
  pendingOnly,
  sortBy,
  sortDir,
}: GetAllHivePaymentsParams): Promise<GetAllHivePaymentsProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();

  const params: Record<string, string> = {
    page: String(page),
    size: String(size),
  };
  if (search) params.search_string = search;
  if (period) params.reference_date = period;
  if (status) params.payment_status = String(status);
  if (pendingOnly) params.pending_only = String(pendingOnly);
  if (sortBy) params.sort_by = sortBy;
  if (sortDir) params.sort_dir = sortDir;

  const queryString = new URLSearchParams(params).toString();
  return fetch(`${baseUrl}/api/V1/hive-payments?${queryString}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const changePaymentStatus = (
  id: number,
  data: { status: PaymentStatus; canceled_refunded_reason?: string }
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/hive-payments/${id}/status`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const addAttachment = (
  id: number,
  data: FormData
): Promise<AddAttachmentProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/hive-payments/${id}/files`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const deleteAttachment = (
  paymentId: number,
  id: number
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'DELETE',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/hive-payments/${paymentId}/files/${id}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const exportFinanceCSV = ({
  search,
  period,
  status,
  pendingOnly,
  sortBy,
  sortDir,
}: ExportCSVParams): Promise<Response> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  const params: Record<string, string> = {};
  if (search) params.search_string = search;
  if (period) params.reference_date = period;
  if (status) params.payment_status = String(status);
  if (pendingOnly) params.pending_only = String(pendingOnly);
  if (sortBy) params.sort_by = sortBy;
  if (sortDir) params.sort_dir = sortDir;

  const queryString = new URLSearchParams(params).toString();
  return fetch(
    `${baseUrl}/api/V1/hive-payments/export-csv?${queryString}`,
    requestOptions
  ).then((response) => response);
};
